import type { ComponentDocs } from '../component-docs.js';
import { SIZE_STEPS } from '../sizes.js';
import { ICON_NAMES } from './icons.js';

/**
 * What `<caos-icon>` says about itself.
 *
 * ── THE NAMES ARE THE VOCABULARY'S, READ AT RUNTIME ──────────────────────────
 * The `name` input offers exactly `ICON_NAMES`, the one list the glyphs are drawn from, so a glyph
 * added to the vocabulary is offered here with nobody editing this file. Browsing the marks with
 * their names is the Icons entry's job; this entry is about placing one.
 *
 * ── SIZES ARE THE PLATFORM SCALE'S ───────────────────────────────────────────
 * The `size` input offers `SIZE_STEPS`, and the box reads `--caos-size-<step>-height`. The icon has
 * no size and no token of its own (CAOS-586, CAOS-594).
 */
const docs: ComponentDocs = {
  aliases: ['glyph', 'symbol', 'pictogram', 'mark'],

  answers: [
    'put an icon on a page',
    'show a small glyph beside a heading',
    'mark a section with the symbol the app already uses for it',
    'make an icon bigger or smaller',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      options: [...ICON_NAMES],
      description:
        'Which glyph to draw, by its name in the platform vocabulary. A name the vocabulary does not have ' +
        'draws nothing, not an empty box.',
    },
    {
      name: 'size',
      attribute: 'size',
      type: 'text',
      options: [...SIZE_STEPS],
      description: 'The square box the glyph fills, from the platform size scale: xs, sm, md, lg or xl.',
      default: 'sm',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What the icon means, for someone who cannot see it. Leave it empty when words beside the icon ' +
        'already say it; the icon is then hidden from assistive technology.',
    },
  ],

  events: [],

  state: [],

  slots: [],

  tokens: SIZE_STEPS.map((step) => ({
    token: `--caos-size-${step}-height`,
    controls: `The length of the square box at size ${step}.`,
  })),

  parts: [{ part: 'icon', is: 'The square box the glyph is drawn in.' }],

  methods: [],

  placing:
    'Inline, in a line of text or beside a heading or a value. It is inline-flex and vertically centred, ' +
    'adds no spacing of its own, and takes the text colour of wherever it is placed, so an icon inside ' +
    'a danger message is drawn in the danger colour without being told.',

  whenToUse:
    'A glyph stands for something on its own, such as a section, a kind of record or a state, and the ' +
    'page author decides it belongs on the page.',

  insteadUse: [
    {
      name: 'Icons',
      when: 'You want to see every glyph the vocabulary has, with its name. That is the reference sheet, not something to place.',
    },
    { name: 'Avatar', when: 'The picture stands for a particular person, with their photo or initials.' },
    { name: 'StatusDot', when: 'The mark is a state colour rather than a symbol.' },
  ],

  accessibility:
    'With a label, the icon is an image with that name. Without one it is hidden from assistive ' +
    'technology entirely, which is right when words beside it already say what it means and wrong when ' +
    'the icon is the only thing saying it: then give it a label. A name the vocabulary does not have ' +
    'draws nothing, and the icon is then hidden even with a label, so a screen reader never announces ' +
    'an empty image. The icon is never focusable and never ' +
    'a control. An icon that does something belongs inside a button.',

  examples: [
    {
      key: 'glyph',
      label: 'A glyph',
      summary: 'One glyph at the default size, labelled for a screen reader.',
      placement: { component: 'caos-icon', inputs: { name: 'building-2', label: 'Account' } },
    },
    {
      key: 'sizes',
      label: 'The size scale',
      summary: 'The same glyph at every step of the platform size scale, from xs to xl.',
      placement: SIZE_STEPS.map((size) => ({ component: 'caos-icon', inputs: { name: 'folder-kanban', size } })),
    },
  ],
};

export default docs;
